import { CommonModule } from "@angular/common";
import { Component, OnInit, inject, signal } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ɵInternalFormsSharedModule,
} from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { map, forkJoin, Observable, lastValueFrom, debounceTime } from "rxjs";
import { PageInfoService } from "src/app/_metronic/layout";
import { AuthService } from "src/app/pages/auth";
import { AuthUtilitiesService } from "src/app/pages/auth/services/auth-utilities.service";

import { BlogService } from "src/app/services/api/blog.service";
import { CartService } from "src/app/services/api/carts.service";
import { CourseService } from "src/app/services/api/courses.service";
import { BlogCategory } from "src/app/services/model/blog-category.model";
import { Blog } from "src/app/services/model/blog.model";
import { ToastService } from "src/app/services/toast-service/toast.service";
import { environment } from "src/environments/environment";
import { SharedModule } from "src/shared/shared.module";
import { PipesModule } from "src/utils/pipes/pipes.module";
import { SkeletonDetailsComponent } from "../skeletons/skeleton-details/skeleton-details.component";
import { SkeletonRightSideComponent } from "../skeletons/skeleton-right-side/skeleton-right-side.component";
import { blogCoverArt, blogCoverTint } from "src/app/config/blog-cover";

@Component({
  selector: "app-blog-detail",
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SharedModule,
    PipesModule,
    TranslateModule,
    SkeletonDetailsComponent,
    SkeletonRightSideComponent,
    ɵInternalFormsSharedModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: "./blog-detail.component.html",
  styleUrl: "./blog-detail.component.scss",
})
export class BlogDetailComponent implements OnInit {
  /** Cover art for a post with no photograph — the same tint and icon the list
   *  card used, so the two screens agree. Defaults to this article. */
  coverTint(id?: number): string {
    return blogCoverTint(id ?? this.blog_info?.id);
  }

  coverArt(id?: number): string {
    return blogCoverArt(id ?? this.blog_info?.id);
  }

  #route = inject(ActivatedRoute);
  #fb = inject(FormBuilder);
  #router = inject(Router);
  #courseService = inject(CourseService);
  #blogService = inject(BlogService);
  #cartService = inject(CartService);
  #toastService = inject(ToastService);
  #translate = inject(TranslateService);

  #pageInfoService = inject(PageInfoService);
  #loginService = inject(AuthUtilitiesService);
  #authService = inject(AuthService);

  course_id = signal<string | undefined>(undefined);

  course_info: any = {};
  blog_info: Blog;
  blogs!: Blog[];
  form: FormGroup;

  isLoading = signal(true);

  /** The id in the URL does not name an article anybody can read. */

  notFound = signal(false);
  searchLoading = signal(false);

  fileUrl = environment.apiFileUrl;
  currentUser = this.#authService.currentUser;

  queryParam: string | null = null;

  constructor() {}

  ngOnInit(): void {
    this.buildForm();
    this.#route.paramMap
      .pipe(map((p) => p.get("blog_id") ?? ""))
      .subscribe((id) => {
        this._setLoading(true, 0);

        this.course_id.set(id);
        this.fetchData();
      });
  }

  buildForm() {
    this.form = this.#fb.group({
      datatable: true,
      page: 1,
      limit: 5,
      keywords: null,
      offset: 0,
      order: "id desc",
    });

    this.form
      .get("keywords")
      ?.valueChanges.pipe(debounceTime(500))
      .subscribe((val) => {
        this.searchBlog();
      });
  }

  _setLoading(value: boolean, time: number = 500) {
    setTimeout(() => {
      this.isLoading.update((_) => value);
    }, time);
  }

  async fetchData() {
    // The article is fetched on its own, not inside the forkJoin.
    //
    // Batched, one bad article id took the recent-posts sidebar down with it —
    // /blogs/999 rendered a header, a footer and twenty-six dangling
    // skeletons. The page around the article does not depend on the article,
    // so it should not fail with it.
    this.#blogService.getBlog(+this.course_id()!).subscribe({
      next: (blog_info: any) => {
        this.blog_info = blog_info;
        this.setBreadCrumb(blog_info);
        this._setLoading(false);
      },
      error: () => {
        this.notFound.set(true);
        this._setLoading(false);
      },
    });

    // Only the recent-posts sidebar. The category list was fetched on every
    // article view and assigned to a field no template on this page reads.
    this.#blogService.getBlogs(this.form.getRawValue()).subscribe({
      next: (blogs: any) => {
        // "บทความล่าสุด" listed the article you were already reading.
        this.blogs = this.#blogService
          .mapImageInnerHtml(blogs.data)
          .filter((item: any) => Number(item?.id) !== Number(this.course_id()));
        this.searchLoading.update((_) => false);
      },
      error: () => this.searchLoading.update((_) => false),
    });
  }

  async searchBlog() {
    this.searchLoading.update((_) => true);

    forkJoin([
      this.#blogService.getBlogs(this.form.getRawValue()) as Observable<any>,
    ]).subscribe({
      next: ([blogs]) => {
        this.blogs = this.#blogService.mapImageInnerHtml(blogs.data);
      },
      error: (err) => {},
      complete: () => {
        this.searchLoading.update((_) => false);
      },
    });
  }

  mapImageInnerHtml(blog_data: Blog[]): Blog[] {
    let blogMap = blog_data.map((item: Blog) => {
      if (!item.article) return;
      const imagePaths = item.article;
      const parser = new DOMParser();
      const doc = parser.parseFromString(imagePaths, "text/html");
      let blog_img = null;
      const elements = Array.from(doc.querySelectorAll("img, oembed"));
      for (const element of elements) {
        if (element.tagName.toLowerCase() === "img") {
          blog_img = element.getAttribute("src");
          break;
        } else if (element.tagName.toLowerCase() === "oembed") {
          const oembedUrl = element.getAttribute("url");
          const youtubeThumbnail = this.#blogService.getYoutubeThumbnail(
            oembedUrl as string,
          );
          if (youtubeThumbnail) {
            blog_img = youtubeThumbnail;
            break;
          }
        }
      }
      return {
        ...item,
        blog_img,
        is_today: moment(item.created_at).isSame(moment(), "day"),
      } as Blog;
    });
    return blogMap as Blog[];
  }

  routerBlog(id: number) {
    this.#router.navigateByUrl(`/blogs/${id}`);
  }

  async setBreadCrumb(info: any) {
    this.#pageInfoService.updateTitle(info.name);

    // if (this.queryParam) {
    var bc = this.#pageInfoService.breadcrumbs.value;
    var newBc = [
      {
        isActive: false,
        isSeparator: false,
        path: `/blogs`,
        title: this.#translate.instant("MENU.BLOG"),
      },
      {
        isActive: false,
        isSeparator: true,
        path: "",
        title: "",
      },
    ];
    bc.push(...newBc);

    // this.#pageInfoService.setBreadcrumbs([]);
    // }
  }
}
